import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  User as FirebaseUser
} from 'firebase/auth';
import { doc, getDoc, setDoc, increment } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { User, MEMBER_TYPES } from '../types/User';

interface AuthContextType {
  user: User | null;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, profileData?: any) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  // Dialog controls (Event triggers)
  openLogin: () => void;
  openRegister: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAdmin: false,
  login: async () => { },
  signup: async () => { },
  logout: async () => { },
  resetPassword: async () => { },
  openLogin: () => { },
  openRegister: () => { },
  loading: true
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Event dispatchers
  const openLogin = () => {
    window.dispatchEvent(new CustomEvent('auth-open-login'));
  };

  const openRegister = () => {
    window.dispatchEvent(new CustomEvent('auth-open-register'));
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUser({
            ...userData,
            id: firebaseUser.uid,
            uid: firebaseUser.uid,
            email: firebaseUser.email || '',
            displayName: firebaseUser.displayName || ''
          } as User);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUser({
          ...userData,
          id: userCredential.user.uid,
          uid: userCredential.user.uid,
          email: userCredential.user.email || '',
          displayName: userCredential.user.displayName || ''
        } as User);
      }
    } catch (error: any) {
      console.error("Login failed:", error);

      // Track failed login attempt in analytics
      try {
        // Ignore localhost
        if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
          const today = new Date().toISOString().split('T')[0];
          const dailyRef = doc(db, 'analytics_daily', today);

          await setDoc(dailyRef, {
            failed_logins: increment(1),
            date: today
          }, { merge: true });
        }
      } catch (analyticsError) {
        console.error("Error logging failed login:", analyticsError);
      }

      throw error;
    }
  };

  const signup = async (email: string, password: string, profileData?: any) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    // Créer l'objet utilisateur à stocker dans Firestore (sans id et uid)
    const userDataForFirestore = {
      email: userCredential.user.email || '',
      firstName: profileData?.firstName || '',
      lastName: profileData?.lastName || '',
      nickname: profileData?.nickname || '',
      city: profileData?.city || '',
      team: profileData?.team || 'Mercenaire Solitaire',
      role: MEMBER_TYPES.BRONZE, // Tout le monde démarre Bronze
      verified: true,
      notificationsEnabled: profileData?.notificationsEnabled !== undefined ? profileData.notificationsEnabled : true, // Par défaut true
      createdAt: new Date().toISOString()
    };

    // Créer l'objet utilisateur complet pour l'application (avec id et uid)
    const newUser: User = {
      ...userDataForFirestore,
      id: userCredential.user.uid,
      uid: userCredential.user.uid
    };

    // Enregistrer uniquement les données nécessaires dans Firestore
    await setDoc(doc(db, 'users', userCredential.user.uid), userDataForFirestore);

    // Définir l'utilisateur complet dans le contexte
    setUser(newUser);
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const value = useMemo(() => ({
    user,
    loading,
    isAdmin: user?.role === MEMBER_TYPES.ADMIN,
    login,
    signup,
    logout,
    resetPassword,
    openLogin,
    openRegister
  }), [user, loading]);

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
